/**
 * The circle beside a contact's name.
 *
 * Clicking it uploads a picture; clicking it once there is one opens the
 * full-size image. Two behaviours on one target, which works because they are
 * the same intention at two moments — "let me see this person" and "here is
 * what they look like" — and because the empty state is unmistakably a button
 * and the filled one is unmistakably a photo.
 *
 * Internal only, and the screen says so. It is not an attachment on the
 * record, it is how the office recognises somebody in a list, and a customer
 * on the portal never sees it.
 */
import { useRef, useState } from 'react'
import { Avatar, Button } from './primitives.js'
import { Modal } from './overlays.js'
import { IconEdit } from '../frame/icons.js'
import { request, upload } from '../lib/api.js'

export function ContactPhoto({
  contactId, name, photoFileId, canEdit, orgUnitId, size = 54, onChanged,
}: {
  contactId: string
  name: string
  photoFileId: string | null
  canEdit: boolean
  orgUnitId: string
  size?: number
  onChanged: () => void
}) {
  const input = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [viewing, setViewing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const src = photoFileId ? `/api/v1/files/${photoFileId}` : undefined

  async function choose(file: File) {
    setBusy(true)
    setError(null)
    try {
      const uploaded = await upload(file, { kind: 'avatar', orgUnitId })
      await request(`/contacts/${contactId}/photo`, {
        method: 'PUT',
        body: { fileId: uploaded.id },
      })
      onChanged()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That picture did not upload.')
    } finally {
      setBusy(false)
      if (input.current) input.current.value = ''
    }
  }

  async function clear() {
    setBusy(true)
    try {
      await request(`/contacts/${contactId}/photo`, { method: 'PUT', body: { fileId: null } })
      setViewing(false)
      onChanged()
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <button
        type="button"
        disabled={busy || (!canEdit && !src)}
        onClick={() => (src ? setViewing(true) : input.current?.click())}
        title={src ? `${name} — click to see it full size` : 'Add a picture'}
        className="group relative rounded-full disabled:cursor-default"
      >
        <Avatar name={name} src={src} size={size} />
        {canEdit && !src && (
          <span
            aria-hidden
            className="absolute inset-0 hidden items-center justify-center rounded-full bg-text/60 text-white group-hover:flex"
          >
            <IconEdit size={16} />
          </span>
        )}
        <span className="sr-only">
          {src ? `${name}'s picture` : `Add a picture of ${name}`}
        </span>
      </button>

      <input
        ref={input}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(event) => {
          const file = event.currentTarget.files?.[0]
          if (file) void choose(file)
        }}
      />

      {error && <p className="m-0 mt-1 text-sub text-danger">{error}</p>}

      <Modal
        open={viewing}
        onClose={() => setViewing(false)}
        title={name}
        footer={
          <>
            {canEdit && (
              <>
                <Button disabled={busy} onClick={() => void clear()}>Remove</Button>
                <Button disabled={busy} onClick={() => input.current?.click()}>Replace</Button>
              </>
            )}
            <Button variant="primary" onClick={() => setViewing(false)}>Close</Button>
          </>
        }
      >
        {src && (
          <img
            src={src}
            alt={name}
            className="mx-auto max-h-photo w-auto rounded-card"
          />
        )}
        <p className="m-0 mt-2 text-center text-sub text-text-faint">
          Seen by everybody here. Never by the customer on the portal.
        </p>
      </Modal>
    </>
  )
}
